// Brand ramp centered on the accent #0078D4 (app.md §7). Setting it as the
// brand ramp's base lets primary actions / selection / highlights read from
// Fluent brand tokens instead of hard-coded hex. Everything else stays neutral.
import { createLightTheme, makeStyles, shorthands, tokens, type BrandVariants, type Theme } from '@fluentui/react-components'

const brand: BrandVariants = {
  10: '#020405',
  20: '#0E1A29',
  30: '#0E2A47',
  40: '#0B3A63',
  50: '#064A80',
  60: '#005B9E',
  70: '#006CBD',
  80: '#0078D4', // rest / primary
  90: '#2E8CDB',
  100: '#4C9CE2',
  110: '#67ACE8',
  120: '#82BCEE',
  130: '#9DCBF3',
  140: '#B7DAF7',
  150: '#D2E9FB',
  160: '#EBF4FE',
}

export const appTheme: Theme = createLightTheme(brand)

// Aging band → color (app.md §8). Saturated hues for the Analytics charts;
// blue stays for actions/selection. Always paired with day-count text.
export const AGE_COLORS: Record<string, string> = {
  neutral: 'transparent',
  yellow: '#F2C744',
  orange: '#E08A2B',
  red: '#D13438',
}

// Tinted dot-pill palette (v1.20 card queue): pale background, darker text,
// solid dot. Green/red double as the semantic good/bad pair (reconcile chip,
// flags pill).
export interface PillColors {
  fg: string
  bg: string
  dot: string
}

export const TINT_PILLS: Record<'green' | 'yellow' | 'orange' | 'red' | 'blue', PillColors> = {
  green: { fg: '#0E700E', bg: '#EBF5EB', dot: '#107C10' },
  yellow: { fg: '#8A6A0C', bg: '#FBF5DC', dot: '#F2C744' },
  orange: { fg: '#9C5B12', bg: '#FDF1E4', dot: '#E08A2B' },
  red: { fg: '#B02A2E', bg: '#FBEDED', dot: '#D13438' },
  // Informational (the manual-entry marker) — brand-tinted, not semantic.
  blue: { fg: '#0F548C', bg: '#EBF3FC', dot: '#0078D4' },
}

// Age band → pill colors. The cards carry no colored border, so the pill alone
// reads the band; the scale runs green (fresh) → red (7+ days).
export const AGE_PILL_COLORS: Record<string, PillColors> = {
  neutral: TINT_PILLS.green,
  yellow: TINT_PILLS.yellow,
  orange: TINT_PILLS.orange,
  red: TINT_PILLS.red,
}

// The bolded label sitting between the reconcile pill (or, on Accounts
// Summary, the "Distribute tax" toggle + pill) and the summary figure it
// names — "Invoice Subtotal:" / "Invoice Total:" (owner, 2026-08-14). One
// shared class so the two hand-rolled copies — `InvoiceLineItemsTable`'s
// `ReconcileFooter` and `AccountsSummary`'s grand-total row — carry the same
// weight rather than drifting. Both those rows are already
// `fontWeightSemibold` at the container level, so this steps up to
// `fontWeightBold` to still read as distinct emphasis next to the figure.
export const useSummaryLabelStyles = makeStyles({
  label: { fontWeight: tokens.fontWeightBold },
})

// Green-filled Approve / red-filled Deny (app.md §9 steps 6–8) — shared by the
// queue row buttons, the modal action bar, and the bulk bar. Disabled states
// fall back to Fluent's neutral disabled look so a greyed button still reads
// as unavailable.
export const useDecisionButtonStyles = makeStyles({
  approve: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.borderColor('transparent'),
    '&:hover': { backgroundColor: tokens.colorPaletteGreenForeground1, color: tokens.colorNeutralForegroundOnBrand, ...shorthands.borderColor('transparent') },
    '&:hover:active': { backgroundColor: tokens.colorPaletteGreenForeground2, color: tokens.colorNeutralForegroundOnBrand, ...shorthands.borderColor('transparent') },
    '&:disabled': { backgroundColor: tokens.colorNeutralBackgroundDisabled, color: tokens.colorNeutralForegroundDisabled, ...shorthands.borderColor(tokens.colorNeutralStrokeDisabled) },
    '&:disabled:hover': { backgroundColor: tokens.colorNeutralBackgroundDisabled, color: tokens.colorNeutralForegroundDisabled, ...shorthands.borderColor(tokens.colorNeutralStrokeDisabled) },
  },
  deny: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.borderColor('transparent'),
    '&:hover': { backgroundColor: tokens.colorPaletteRedForeground1, color: tokens.colorNeutralForegroundOnBrand, ...shorthands.borderColor('transparent') },
    '&:hover:active': { backgroundColor: tokens.colorPaletteRedForeground2, color: tokens.colorNeutralForegroundOnBrand, ...shorthands.borderColor('transparent') },
    '&:disabled': { backgroundColor: tokens.colorNeutralBackgroundDisabled, color: tokens.colorNeutralForegroundDisabled, ...shorthands.borderColor(tokens.colorNeutralStrokeDisabled) },
    '&:disabled:hover': { backgroundColor: tokens.colorNeutralBackgroundDisabled, color: tokens.colorNeutralForegroundDisabled, ...shorthands.borderColor(tokens.colorNeutralStrokeDisabled) },
  },
})
